import type {
  ApiResponseOptionDetailResponse,
  ApiResponseOptionDetailList,
  CreateOptionRequest,
  UpdateOptionRequest,
  CreateAttributeRequest,
  UpdateAttributeRequest,
  CreateValueRequest,
  UpdateValueRequest,
  CreateCombinationRequest,
  UpdateCombinationRequest,
  CreateCombinationKeyRequest,
  BulkCreateCombinationKeyRequest,
  CreatePriceTierRequest,
  BulkCreatePriceTierRequest,
  BulkUpsertPriceTierRequest,
  OptionDetailResponse,
} from "../../../shared/types/api";
import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";

export class CompanyOptionApiService {
  // Option CRUD
  async getOptions(companyId: number): Promise<ApiResponseOptionDetailList> {
    const response = await httpClient.get<ApiResponseOptionDetailList>(
      NEW_API_ENDPOINTS.companyOptionController.GET(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션 목록을 가져오지 못했습니다.");
    }

    return response.data;
  }

  async getOptionDetail(companyId: number, optionId: number): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.get<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.GET_DETAIL(companyId, optionId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션 상세 정보를 가져오지 못했습니다.");
    }

    return response.data;
  }

  async createOption(companyId: number, request: CreateOptionRequest): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.post<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.POST(companyId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션을 생성하지 못했습니다.");
    }

    return response.data;
  }

  async updateOption(companyId: number, optionId: number, request: UpdateOptionRequest): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.put<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.PUT(companyId, optionId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션을 수정하지 못했습니다.");
    }

    return response.data;
  }

  async deleteOption(companyId: number, optionId: number): Promise<void> {
    const response = await httpClient.delete(
      NEW_API_ENDPOINTS.companyOptionController.DELETE(companyId, optionId),
    );

    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "옵션을 삭제하지 못했습니다.");
    }
  }

  // Attribute CRUD
  async addAttribute(companyId: number, optionId: number, request: CreateAttributeRequest): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.post<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.POST_ATTRIBUTE(companyId, optionId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "항목을 추가하지 못했습니다.");
    }

    return response.data;
  }

  async updateAttribute(
    companyId: number,
    optionId: number,
    attributeId: number,
    request: UpdateAttributeRequest
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.put<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.PUT_ATTRIBUTE(companyId, optionId, attributeId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "항목을 수정하지 못했습니다.");
    }

    return response.data;
  }

  async deleteAttribute(companyId: number, optionId: number, attributeId: number): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.delete<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.DELETE_ATTRIBUTE(companyId, optionId, attributeId),
    );

    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "항목을 삭제하지 못했습니다.");
    }

    return response.data;
  }

  // Value CRUD (multipart/form-data 지원)
  async addValue(
    companyId: number,
    optionId: number,
    attributeId: number,
    request: CreateValueRequest,
    imageFile?: File
  ): Promise<ApiResponseOptionDetailResponse> {
    const formData = new FormData();
    formData.append(
      "request",
      new Blob([JSON.stringify(request)], { type: "application/json" })
    );
    if (imageFile) {
      formData.append("imageFile", imageFile);
    }

    const response = await httpClient.post<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.POST_VALUE(companyId, optionId, attributeId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "값을 추가하지 못했습니다.");
    }

    return response.data;
  }

  async updateValue(
    companyId: number,
    optionId: number,
    attributeId: number,
    valueId: number,
    request: UpdateValueRequest & { deleteImage?: boolean },
    imageFile?: File
  ): Promise<ApiResponseOptionDetailResponse> {
    const formData = new FormData();
    formData.append(
      "request",
      new Blob([JSON.stringify(request)], { type: "application/json" })
    );
    if (imageFile) {
      formData.append("imageFile", imageFile);
    }

    const response = await httpClient.put<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.PUT_VALUE(companyId, optionId, attributeId, valueId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "값을 수정하지 못했습니다.");
    }

    return response.data;
  }

  async deleteValue(
    companyId: number,
    optionId: number,
    attributeId: number,
    valueId: number
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.delete<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.DELETE_VALUE(companyId, optionId, attributeId, valueId),
    );

    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "값을 삭제하지 못했습니다.");
    }

    return response.data;
  }

  // Combination CRUD
  async createCombination(
    companyId: number,
    optionId: number,
    request: CreateCombinationRequest
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.post<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.POST_COMBINATION(companyId, optionId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "조합을 생성하지 못했습니다.");
    }

    return response.data;
  }

  async updateCombination(
    companyId: number,
    optionId: number,
    combinationId: number,
    request: UpdateCombinationRequest
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.put<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.PUT_COMBINATION(companyId, optionId, combinationId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "조합을 수정하지 못했습니다.");
    }

    return response.data;
  }

  async deleteCombination(
    companyId: number,
    optionId: number,
    combinationId: number
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.delete<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.DELETE_COMBINATION(companyId, optionId, combinationId),
    );

    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "조합을 삭제하지 못했습니다.");
    }

    return response.data;
  }

  // CombinationKey CRUD
  async createCombinationKey(
    companyId: number,
    optionId: number,
    combinationId: number,
    request: CreateCombinationKeyRequest
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.post<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.POST_COMBINATION_KEY(companyId, optionId, combinationId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "조합키를 생성하지 못했습니다.");
    }

    return response.data;
  }

  async bulkCreateCombinationKeys(
    companyId: number,
    optionId: number,
    combinationId: number,
    request: BulkCreateCombinationKeyRequest
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.post<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.POST_COMBINATION_KEY_BULK(companyId, optionId, combinationId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "조합키를 일괄 생성하지 못했습니다.");
    }

    return response.data;
  }

  async deleteCombinationKey(
    companyId: number,
    optionId: number,
    combinationId: number,
    keyId: number
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.delete<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.DELETE_COMBINATION_KEY(companyId, optionId, combinationId, keyId),
    );

    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "조합키를 삭제하지 못했습니다.");
    }

    return response.data;
  }

  // PriceTier CRUD
  async createPriceTier(
    companyId: number,
    optionId: number,
    combinationId: number,
    keyId: number,
    request: CreatePriceTierRequest
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.post<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.POST_PRICE_TIER(companyId, optionId, combinationId, keyId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "가격 구간을 추가하지 못했습니다.");
    }

    return response.data;
  }

  async bulkCreatePriceTiers(
    companyId: number,
    optionId: number,
    combinationId: number,
    keyId: number,
    request: BulkCreatePriceTierRequest
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.post<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.POST_PRICE_TIER_BULK(companyId, optionId, combinationId, keyId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "가격 구간을 일괄 추가하지 못했습니다.");
    }

    return response.data;
  }

  async upsertPriceTiers(
    companyId: number,
    optionId: number,
    combinationId: number,
    keyId: number,
    request: BulkUpsertPriceTierRequest
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.put<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.PUT_PRICE_TIER_BULK(companyId, optionId, combinationId, keyId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "가격 구간을 저장하지 못했습니다.");
    }

    return response.data;
  }

  async updatePriceTier(
    companyId: number,
    optionId: number,
    combinationId: number,
    keyId: number,
    tierId: number,
    request: CreatePriceTierRequest
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.put<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.PUT_PRICE_TIER(companyId, optionId, combinationId, keyId, tierId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "가격 구간을 수정하지 못했습니다.");
    }

    return response.data;
  }

  async deletePriceTier(
    companyId: number,
    optionId: number,
    combinationId: number,
    keyId: number,
    tierId: number
  ): Promise<ApiResponseOptionDetailResponse> {
    const response = await httpClient.delete<ApiResponseOptionDetailResponse>(
      NEW_API_ENDPOINTS.companyOptionController.DELETE_PRICE_TIER(companyId, optionId, combinationId, keyId, tierId),
    );

    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "가격 구간을 삭제하지 못했습니다.");
    }

    return response.data;
  }
}

export const companyOptionApiService = new CompanyOptionApiService();
